// web/src/App.tsx
import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import { AuthProvider, useAuth } from '@/hooks/use-auth'
import { useLang } from '@/hooks/use-lang'
// Synchronous imports: routes hit on every session or cheap enough that lazy
// isn't worth the flicker (login, signup, shell, core data pages).
import { LoginPage } from '@/pages/login'
import { SignupPage } from '@/pages/signup'
import { ForgotPasswordPage } from '@/pages/forgot-password'
import { ResetPasswordPage } from '@/pages/reset-password'
import { Layout } from '@/components/layout'
import { EventsPage } from '@/pages/events'
import { EndpointsPage } from '@/pages/endpoints'
import { AuthCallbackPage } from '@/pages/auth-callback'
import { OnboardingPage } from '@/pages/onboarding'
import { OAuthLoginPage } from '@/pages/oauth-login'
import { OAuthConsentPage } from '@/pages/oauth-consent'
import { api, ApiError } from '@/lib/api'
// Lazy routes: Analytics pulls Recharts (~120KB), Help hosts heavy slide
// content, Legal + Audit + Admin are low-frequency. React.lazy cuts the
// initial bundle so first paint doesn't carry weight the user may never use.
const EventDetailPage = lazy(() => import('@/pages/event-detail').then(m => ({ default: m.EventDetailPage })))
const EndpointDetailPage = lazy(() => import('@/pages/endpoint-detail').then(m => ({ default: m.EndpointDetailPage })))
const HelpPage = lazy(() => import('@/pages/help').then(m => ({ default: m.HelpPage })))
const AnalyticsPage = lazy(() => import('@/pages/analytics').then(m => ({ default: m.AnalyticsPage })))
const APIKeysPage = lazy(() => import('@/pages/api-keys').then(m => ({ default: m.APIKeysPage })))
const SettingsPage = lazy(() => import('@/pages/settings').then(m => ({ default: m.SettingsPage })))
const SupportPage = lazy(() => import('@/pages/support').then(m => ({ default: m.SupportPage })))
const AlertsPage = lazy(() => import('@/pages/alerts').then(m => ({ default: m.AlertsPage })))
const AuditPage = lazy(() => import('@/pages/audit').then(m => ({ default: m.AuditPage })))
const TermsPage = lazy(() => import('@/pages/terms').then(m => ({ default: m.TermsPage })))
const PrivacyPage = lazy(() => import('@/pages/privacy').then(m => ({ default: m.PrivacyPage })))
const DpaPage = lazy(() => import('@/pages/dpa').then(m => ({ default: m.DpaPage })))
import { ErrorBoundary } from '@/components/error-boundary'
import { ToastProvider, useToast } from '@/components/toast-provider'
import type { Tenant } from '@/lib/types'

// RouteErrorBoundary wraps each route so a bug in one page doesn't take down
// the whole shell (sidebar, toasts stay alive); the top-level boundary then
// acts as the final safety net for anything that escapes.
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  return <ErrorBoundary>{children}</ErrorBoundary>
}

// ToastWireup makes the toast API available to React Query mutations that
// don't define their own onError. Errors from fetchWithAuth surface as
// ApiError so the message is already human-friendly.
function GlobalMutationErrorToast() {
  const toast = useToast()
  useEffect(() => {
    queryClient.getMutationCache().config.onError = (err) => {
      if (err instanceof ApiError) {
        // 401 is handled by api.ts redirect; avoid double-toasting the login jump.
        if (err.status !== 401) toast.error(err.message)
      } else if (err instanceof Error) {
        toast.error(err.message)
      }
    }
    return () => {
      queryClient.getMutationCache().config.onError = undefined
    }
  }, [toast])
  return null
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
})

// Onboarding is zero-step: signup (and GitHub OAuth) auto-provision the
// workspace plus a first inspect-only endpoint and mark onboarding complete,
// so there is no forced setup wizard. New users land straight in the app and
// the events page's first-run empty state guides them (paste the URL, watch
// events land). Kept as a thin pass-through so the route tree stays untouched.
function OnboardingGuard({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { token, loading } = useAuth()
  const location = useLocation()
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>
  // Preserve the originally-requested URL (pathname + search) so login can send
  // the user back here. Critical for the OAuth flow: Hydra redirects to
  // /oauth/login?login_challenge=… and the challenge must survive the login bounce.
  if (!token) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  return <>{children}</>
}

function LangManager() {
  const lang = useLang()
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])
  return null
}

function RouteFallback() {
  return (
    <div style={{
      minHeight: '40vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--muted)', fontSize: '0.875rem',
    }}>
      Loading…
    </div>
  )
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <GlobalMutationErrorToast />
          <AuthProvider>
            <BrowserRouter>
              <LangManager />
              <OnboardingGuard>
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/dpa" element={<DpaPage />} />
                    <Route path="/auth/callback" element={<AuthCallbackPage />} />
                    <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
                    <Route path="/oauth/login" element={<ProtectedRoute><OAuthLoginPage /></ProtectedRoute>} />
                    <Route path="/oauth/consent" element={<ProtectedRoute><OAuthConsentPage /></ProtectedRoute>} />
                    <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                      {/* Root → Stream. Was Flow until cut in commit XXX (Flow
                          was a visual flair without a clear differentiation
                          vs the Endpoints + Inspector views; cut to focus the
                          first-view on real activity, not concepts). */}
                      <Route index element={<RouteErrorBoundary><EventsPage /></RouteErrorBoundary>} />
                      <Route path="events/:id" element={<RouteErrorBoundary><EventDetailPage /></RouteErrorBoundary>} />
                      <Route path="endpoints" element={<RouteErrorBoundary><EndpointsPage /></RouteErrorBoundary>} />
                      <Route path="endpoints/:id" element={<RouteErrorBoundary><EndpointDetailPage /></RouteErrorBoundary>} />
                      <Route path="help" element={<RouteErrorBoundary><HelpPage /></RouteErrorBoundary>} />
                      <Route path="analytics" element={<RouteErrorBoundary><AnalyticsPage /></RouteErrorBoundary>} />
                      <Route path="api-keys" element={<RouteErrorBoundary><APIKeysPage /></RouteErrorBoundary>} />
                      <Route path="alerts" element={<RouteErrorBoundary><AlertsPage /></RouteErrorBoundary>} />
                      <Route path="audit" element={<RouteErrorBoundary><AuditPage /></RouteErrorBoundary>} />
                      <Route path="settings" element={<RouteErrorBoundary><SettingsPage /></RouteErrorBoundary>} />
                      <Route path="support" element={<RouteErrorBoundary><SupportPage /></RouteErrorBoundary>} />
                    </Route>
                  </Routes>
                </Suspense>
              </OnboardingGuard>
            </BrowserRouter>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
